import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react';

import { useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { debounceDelay } from '../../../constants';
import { DevelopmentPlanSkillForm } from '../../../containers';
import { transformSkillBaseToSearchableOption } from '../../../models/@competentNL/Skills/SkillsTransformers';
import { AddDevelopmentPlanSkillFormData } from '../../../models/DevelopmentPlanSkill/DevelopmentPlanSkill';
import { fetchCompetentNLSkills } from '../../../redux/competentNL/competentNLActions';
import { addDevelopmentPlanSkill } from '../../../redux/developmentPlanSkill/developmentPlanSkillActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';
import { SearchableOption } from '../../../types';

interface ConnectedDevelopmentPlanSkillFormProps {
    onClose: () => void;
    className?: string;
}

const ConnectedDevelopmentPlanSkillForm: FC<ConnectedDevelopmentPlanSkillFormProps> = ({
    onClose,
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();
    const { candidateUuid } = useParams<{ 'candidateUuid': string }>();

    const isLoadingDevelopmentPlanCompetencies = useTypedSelector(state => state.developmentPlanSkillReducer.isLoading);
    const developmentPlan = useTypedSelector(state => state.developmentPlanReducer.developmentPlan);

    const skillsIsLoading = useTypedSelector(state => state.competentNLReducer.skillsIsLoading);
    const skillBases = useTypedSelector(state => state.competentNLReducer.skillBases);
    const skillsError = useTypedSelector(state => state.competentNLReducer.skillsError);

    const [skillQuery, setSkillQuery] = useState<string>('');

    const skillOptions = useMemo((): SearchableOption[] => (
        skillBases.map(transformSkillBaseToSearchableOption)
    ), [skillBases]);

    useDebounce((): void => {
        if (skillQuery.length > 1) {
            dispatch(fetchCompetentNLSkills(skillQuery));
        }
    }, debounceDelay, [skillQuery]);

    const handleSubmitAddSkill = (formData: AddDevelopmentPlanSkillFormData): void => {
        if (candidateUuid) {
            dispatch(addDevelopmentPlanSkill(formData, candidateUuid));
            onClose();
        }
    };

    return (
        <DevelopmentPlanSkillForm
            isLoading={isLoadingDevelopmentPlanCompetencies}
            isLoadingSkills={skillsIsLoading}
            developmentPlanId={developmentPlan?.id || ''}
            skillOptions={skillOptions}
            skillsError={skillsError}
            onSkillSearchChange={setSkillQuery}
            onCancel={onClose}
            onSubmit={handleSubmitAddSkill}
            className={className}
        />
    );
};

export default ConnectedDevelopmentPlanSkillForm;
